import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import React from "react"
import { css } from "styled-components"
import Divider from "../atoms/divider"
import { Block, Spacer } from "../atoms/layout-primitives"
import Link from "../atoms/link"
import VariantContainer from "../atoms/variant-container"
import { IArticle } from "../molecules/article-card"
import ArticleCardGrid from "../organisms/article-card-grid"
import { BT } from "../styles/blog-typography"
import { IContentfulComposePage, IContentfulTag } from "../types/blog.types"
import { contentfulBlogPostToArticleCard } from "../util/contentful-data"
import InternalRoutes from "../util/internal-routes"

interface ITopicRowProps {
  category: IContentfulTag
  topic: IContentfulTag
  articles: { node: IContentfulComposePage }[]
  isVisible?: boolean
  articleCount: number
}

/** Slick-dots height from bottom of slider */
const dotsHeight = "24px"

/**
 * Template for a row of at most 3 articles in a category topic
 * @param category - The category of this topic
 * @param topic - The topic
 * @param articles - The 3 articles in this topic
 * @param isVisible - Whether or not this topic row is visible
 * @param articleCount - The total number of articles in this topic
 */
const TopicRow = ({
  category,
  topic,
  articles = [],
  isVisible = true,
  articleCount = 0,
}: ITopicRowProps) => {
  const articleCardsData: IArticle[] =
    articles &&
    articles.map(({ node: article }) =>
      contentfulBlogPostToArticleCard(article)
    )

  return (
    <Block
      variants={[
        css`
          max-height: ${isVisible ? "100%" : "0px"};
          visibility: ${isVisible ? "visible" : "hidden"};
          opacity: ${isVisible ? "1" : "0"};
          overflow: ${isVisible ? "visible" : "hidden"};
        `,
      ]}
    >
      <VariantContainer
        variants={[
          css`
            display: inline;
            padding-right: 12px;
            ${BT.sm.h3`500`};
          `,
          css`
            ${BT.md.h3`500`};
          `,
        ]}
      >
        {topic.name} {`(${articleCount})`}
      </VariantContainer>
      <VariantContainer
        variants={[
          css`
            display: inline;
            ${BT.sm.info`400`};
            color: var(--burnt-orange);
            a {
              text-decoration: none;
              color: inherit;
            }
          `,
        ]}
      >
        <Link to={InternalRoutes.Blog(category, topic)}>See All</Link>
      </VariantContainer>
      <Spacer height={{ sm: "8px", md: "16px" }} />
      <Divider />
      <Spacer height={{ sm: "24px", md: "32px" }} />
      <ArticleCardGrid
        articles={articleCardsData}
        overflow={"carousel"}
        dotsHeight={dotsHeight}
        align={"left"}
      />
      <Spacer
        height={{
          sm: `calc(64px - ${dotsHeight})`,
        }}
      />
    </Block>
  )
}

export default TopicRow
